import React from 'react';
import { ColumnDef } from '@tanstack/react-table';
import 'moment/locale/is';
import {
  TravelExpenseResults,
  TravelExpensesFormQuery,
} from 'api/services/TravelExpenses';
import { Box, Tooltip, Text as TextComponent } from '@chakra-ui/react';
import { IconChatbox, IconDocument } from 'components/Icons';
import { downloadFileFromBase64 } from 'utils/base64ToBlob';

const formatNumberWithDotSeparator = (number: number): string => {
  return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
};

export const columns = (
  amountTotal: number
): ColumnDef<TravelExpensesFormQuery>[] => {
  return [
    {
      accessorKey: 'costDescription',
      header: () => <span>Kostnaður</span>,
      accessorFn: (row) => row,
      cell: ({ getValue }): JSX.Element => {
        const { costType, carbonEmission, comment } =
          getValue() as TravelExpenseResults;
        return (
          <Box display="flex">
            {costType}
            {carbonEmission !== 0 && (
              <Tooltip label="Kolefnisjöfnun flugs">
                <span style={{ color: 'gray', fontSize: '90%' }}>
                  {' '}
                  - ({carbonEmission} CO₂)
                </span>
              </Tooltip>
            )}
            {comment && (
              <Tooltip
                placement="top"
                bg="bg-default"
                color="font-light"
                fontSize={15}
                fontWeight={400}
                padding="20px 25px 25px"
                boxShadow=" 0 1px 3px 0 rgba(0, 0, 0, 0.06), 0 8px 20px 0 rgba(0, 0, 0, 0.15)"
                label={
                  <>
                    <TextComponent>
                      <strong>Athugasemd:</strong>
                    </TextComponent>
                    {comment}
                  </>
                }
              >
                <IconChatbox
                  width={20}
                  cursor="pointer"
                  style={{ marginLeft: 10 }}
                />
              </Tooltip>
            )}
          </Box>
        );
      },
    },
    {
      accessorKey: 'paymentMethodDescription',
      header: () => <span>Greiðsluháttur</span>,
    },
    {
      accessorKey: 'attachments',
      accessorFn: (row) => row,
      header: () => <span>Viðhengi</span>,
      cell: ({ getValue }): JSX.Element => {
        const { attachments } = getValue() as TravelExpenseResults;
        return (
          <Box display="flex" flexDir="column" cursor="pointer">
            {attachments?.map((attachment) => (
              <Box
                display="flex"
                flexDir="row"
                cursor="pointer"
                gap={5}
                key={attachment.fileName}
              >
                <Tooltip label={attachment.fileName}>
                  <Box
                    display={'flex'}
                    flexDir="row"
                    onClick={(): void =>
                      downloadFileFromBase64(
                        attachment.fileContents,
                        attachment.fileName
                      )
                    }
                  >
                    <IconDocument width={20} />
                    <span style={{ marginLeft: '5px' }}>{attachment.name}</span>
                  </Box>
                </Tooltip>
              </Box>
            ))}
          </Box>
        );
      },
    },
    {
      accessorKey: 'amount',
      header: () => (
        <Box display="flex" w="100%" justifyContent="end">
          Upphæð
        </Box>
      ),
      footer: (): JSX.Element => (
        <Box display="flex" w="100%" justifyContent="end">
          Samtals: {formatNumberWithDotSeparator(amountTotal)} ISK
        </Box>
      ),
      accessorFn: (row) => row,
      cell: ({ getValue }): JSX.Element => {
        const { amount, currency } = getValue() as TravelExpensesFormQuery;
        return (
          <Box float="right">
            {formatNumberWithDotSeparator(amount)} {currency}
          </Box>
        );
      },
    },
  ];
};
